import './ProjectItem.scss';

import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { memo } from 'react'
import { Badge, Card, Col, Row } from 'react-bootstrap'
import UserItem from '../common/UserItem';
import { formatDate } from '../../utils';
import { Project } from '../../models/Project';
import { Tooltip } from 'antd';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

type Props = {
  project: Project;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  onProjectEdit: React.MouseEventHandler<HTMLButtonElement>;
  onProjectDelete: React.MouseEventHandler<HTMLButtonElement>;
}

function ProjectItem({ project, onClick, onProjectEdit, onProjectDelete }: Props) {
  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));
  const datasetsLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasets"));
  const servicesLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "services"));
  const modelsLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "models"));

  return (
    <Card className="project-item" onClick={onClick}>
      <Card.Header className="project-item-header bg-project">
        <FontAwesomeIcon className="project-icon" icon={solid('folder')} />

        <div className="position-relative">
          <Row className="gx-0 align-items-start">
            <Col className="project-date">
              <b>Created</b>&nbsp;
              <span>{formatDate(project.createDateTime!)}</span>
            </Col>

            <Badge className="shadow w-auto">{project.accessLevel}</Badge>
          </Row>

          <div className="text-center py-3">
            <Tooltip title={project.name} mouseEnterDelay={0.3}>
              <h5 className="project-title">{project.name}</h5>
            </Tooltip>
            <p className="project-description line-clamp-2 mt-2">{project.description}</p>
          </div>

          <Row className="gx-0 justify-content-center">
            <Col className="text-center" xs={9}>
              <Tooltip title="Edit" mouseEnterDelay={0.3}>
                <button className="circle-button ms-2" onClick={onProjectEdit}>
                  <FontAwesomeIcon size="sm" icon={solid('pen')} />
                </button>
              </Tooltip>
              <Tooltip title="Delete" mouseEnterDelay={0.3}>
                <button className="circle-button ms-2" onClick={onProjectDelete}>
                  <FontAwesomeIcon size="sm" icon={solid('trash')} />
                </button>
              </Tooltip>
            </Col>
          </Row>
        </div>
      </Card.Header>
      <Card.Body className="p-2">
        <UserItem className="mb-2" user={project.owner!} organization={project.organization!} />

        {/* <div className="d-flex">
          {project.tags.length === 0 && <Badge className="m-1 bg-secondary" pill={true}>0 tags</Badge>}
          {project.tags.map((tag, index) => <Badge key={index} className="m-1" pill={true}>{tag}</Badge>)}
        </div> */}

        <Row>
          <Col xs={3} className="text-center py-2">
            <Tooltip title={applicationLabels.plural} mouseEnterDelay={0.25}>
              <div className="project-stat">
                <FontAwesomeIcon icon={solid('laptop-code')} fixedWidth />
                <span className="ms-2">{project.bdaApplications?.length || 0}</span>
              </div>
            </Tooltip>
          </Col>
          <Col xs={3} className="text-center py-2">
            <Tooltip title={datasetsLabels.plural} mouseEnterDelay={0.25}>
              <div className="project-stat">
                <FontAwesomeIcon icon={solid('database')} fixedWidth />
                <span className="ms-2">{project.datasets?.length || 0}</span>
              </div>
            </Tooltip>
          </Col>
          <Col xs={3} className="text-center py-2">
            <Tooltip title={modelsLabels.plural} mouseEnterDelay={0.25}>
              <div className="project-stat">
                <FontAwesomeIcon icon={solid('graduation-cap')} fixedWidth />
                <span className="ms-2">{project.models?.length || 0}</span>
              </div>
            </Tooltip>
          </Col>
          <Col xs={3} className="text-center py-2">
            <Tooltip title={servicesLabels.plural} mouseEnterDelay={0.25}>
              <div className="project-stat">
                <FontAwesomeIcon icon={solid('cubes')} fixedWidth />
                <span className="ms-2">{project.services?.length || 0}</span>
              </div>
            </Tooltip>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

export default memo(ProjectItem);